import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FormTemplateQuestion,
  FormAnswer,
  FormTemplateQuestionFieldsKindEnum,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import { PlanState } from '@lib/enums/plan';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Table from '@lib/components/Table/Table';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useTaskCompletionQueryHook from 'utils/fetch/hooks/useTaskCompletionQueryHook';
import { APP_URLS } from 'constants/urls';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import mergeTaskQuestionsWithAnswers from './utils/mergeTaskQuestionsWithAnswers';
import getTaskCompletionDetailsTableColumns from './utils/getTaskCompletionDetailsTableColumns';
import useTaskCompletionDetailsActions from './utils/useTaskCompletionDetailsActions';
import TaskCompletionModalHeader from './TaskCompletionModalHeader';
import {
  FormTemplateQuestionWithAnswers,
  TaskCompletionDetailsTableRow,
} from './types';
import getTableRowClassNameByValidityHint from './utils/getTableRowClassNameByValidityHint';
import styles from './TaskCompletionDetails.module.scss';

type Props = {
  completionId: string | null;
  onChangeCompletionId: (id: string) => void;
  fetchIds?: () => Promise<FetchIdsResponse<unknown>>;
};

export default function TaskCompletionDetailsContent(props: Props) {
  const { completionId, onChangeCompletionId, fetchIds } = props;

  const { t } = useTranslation();
  const {
    getUserAccessByCompany,
    handleOpenPlanModal,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isViewLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  const onUpgradePlan = useCallback(() => {
    handleOpenPlanModal(PlanState.Locked, [
      CompanyPlanUpgradeFeatureEnum.Documentation,
    ]);
  }, [handleOpenPlanModal]);

  const onDocumentClick = useCallback((documentationId: string) => {
    window.open(
      APP_URLS.dashboard.documentation.view.getDynamicPath({
        pathParts: { id: documentationId },
      }),
      '_blank',
    );
  }, []);

  const {
    allIds,
    onNavigatePrev,
    onNavigateNext,
    canNavigatePrev,
    canNavigateNext,
  } = useTaskCompletionDetailsActions({
    completionId,
    fetchIds,
    onChangeCompletionId,
  });

  const { data, loading, firstLoading, error } = useTaskCompletionQueryHook({
    id: completionId || '',
    isViewLocked,
  });

  const answers = data?.answers as FormAnswer[];
  const questions = data?.task?.formTemplateVersion
    .questions as FormTemplateQuestion[];

  const mergedQuestions = useMemo<FormTemplateQuestionWithAnswers[]>(
    () =>
      mergeTaskQuestionsWithAnswers(questions, answers, [
        FormTemplateQuestionFieldsKindEnum.Section,
      ]),
    [answers, questions],
  );

  // Render every Document field (even when EDM is locked) so the row is
  // visible — DocumentResponseCell handles the locked-state UI per role:
  // yellow SupportiveMessage + Upgrade plan for Full Admin, gray
  // `document-unavailable` SupportiveMessage for User role.
  const tableData = useMemo<TaskCompletionDetailsTableRow[]>(
    () =>
      mergedQuestions.flatMap((question) =>
        question.fields.map((field) => ({
          ...field,
          questionName: question.name,
        })),
      ),
    [mergedQuestions],
  );

  // Index reading duration by `documentationId` across ALL answers — used by
  // DocumentResponseCell to recover reading time on `READ_PREVIOUSLY` rows
  // whose view was attached to a sibling answer (the per-resp view id is null
  // on those rows by BE design).
  const readingDurationByDocumentationId = useMemo(() => {
    const map = new Map<string, number>();
    (answers || []).forEach((answer) => {
      (answer?.documentationVersionViews || []).forEach((view) => {
        if (
          view?.documentationId &&
          typeof view.readingDurationSeconds === 'number' &&
          !map.has(view.documentationId)
        ) {
          map.set(view.documentationId, view.readingDurationSeconds);
        }
      });
    });
    return map;
  }, [answers]);

  const columns = useMemo(
    () =>
      getTaskCompletionDetailsTableColumns({
        t,
        readingDurationByDocumentationId,
        onDocumentClick,
        isViewLocked,
        isFullAdmin,
        onUpgradePlan,
      }),
    [
      t,
      readingDurationByDocumentationId,
      onDocumentClick,
      isViewLocked,
      isFullAdmin,
      onUpgradePlan,
    ],
  );

  return (
    <div className={styles.root}>
      <TaskCompletionModalHeader
        data={data}
        loading={loading}
        allIds={allIds}
        onNavigatePrev={onNavigatePrev}
        onNavigateNext={onNavigateNext}
        canNavigatePrev={canNavigatePrev}
        canNavigateNext={canNavigateNext}
      />
      <div className={styles.tableWrapper}>
        {error && (
          <ErrorPage
            description={getDescriptionFromError(error)}
            status={500}
          />
        )}
        {firstLoading && <CircleLoader />}
        {!error && !firstLoading && (
          <Table<TaskCompletionDetailsTableRow>
            id="task-completion-tasks"
            columns={columns}
            data={tableData}
            totalEntries={tableData.length}
            loading={loading}
            getRowClassName={getTableRowClassNameByValidityHint}
          />
        )}
      </div>
    </div>
  );
}
